import theme from 'lib/theme'
import { SetStateAction, Dispatch, useState } from 'react'
import { ArrowForward } from '@mui/icons-material'
import { SelectDropdown } from 'components/SelectDropdown/SelectDropdown'
import { Box, Typography, useMediaQuery } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'providers/AuthProvider'
import { useGetUserPlaces } from 'hooks/usePlaces'
import SearchWithSuggestions from 'components/SearchWithSuggestions/SearchWithSuggestions'
import { ConfigFieldTypes } from './SearchConfig'
import { SearchBar } from './SearchBar'
import { ForwardButtonWrapper } from './SearchConfig.styled'

type SettingsFormProps = {
  values: ConfigFieldTypes
  categoryTrees: { label: string; value: string }[]
  setFieldValue: (
    field: string,
    value: string | number | { label: string; value: string }
  ) => void
  submitForm: (values: ConfigFieldTypes) => void
  setPage: Dispatch<SetStateAction<number>>
  setSelectedPlace: Dispatch<
    SetStateAction<{ label: string; value: string } | null>
  >
  setShowOverlay: Dispatch<SetStateAction<boolean>>
}

export const SettingsForm = ({
  setFieldValue,
  values,
  categoryTrees,
  submitForm,
  setPage,
  setSelectedPlace,
  setShowOverlay,
}: SettingsFormProps) => {
  const { t } = useTranslation()
  const { userStores } = useAuth()
  const [query, setQuery] = useState<string | null>(null)
  const { isLoading, places, error } = useGetUserPlaces(userStores, query)
  const [openSelectPlaces, setOpenSelectPlaces] = useState(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const smallerScreen = useMediaQuery(theme.breakpoints.down('lg'))

  const handleSelectPlace = (option: { label: string; value: string }) => {
    setSelectedPlace(option)
    setFieldValue('store', option)
  }

  const clearSearch = () => {
    setQuery(null)
    setFieldValue('store', { label: '', value: '' })
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.action.selected,
        paddingRight: smallerScreen ? theme.spacing(2) : 0,
        paddingLeft: smallerScreen ? theme.spacing(2) : 0,
      }}
      height={smallerScreen ? '316px' : '150px'}>
      <Box pt={1} pl={2}>
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: '18px',
          }}>
          {t('emulate')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: smallerScreen ? 'column' : 'row',
        }}
        justifyContent='space-around'
        alignItems={smallerScreen ? 'flex-start' : 'center'}>
        <Box
          width={smallerScreen ? '100%' : '30%'}
          height={theme.spacing(6)}
          sx={{ marginTop: smallerScreen ? '16px' : 0 }}>
          <SearchWithSuggestions
            label={t('store')}
            id='store'
            options={places[0]?.label?.length > 0 ? places : []}
            loading={isLoading}
            onSelect={({ option }) => handleSelectPlace(option)}
            error={Boolean(error)}
            onChange={(value: string) => setQuery(value)}
            isOpen={openSelectPlaces}
            setIsOpen={setOpenSelectPlaces}
            placeholderText={t('selectStore')}
            helperText={t('enterAtLeastThreeCharacters')}
            query={query}
            clearSearch={clearSearch}
            selectedValue={values.store}
          />
        </Box>
        <Box
          width={smallerScreen ? '100%' : '30%'}
          sx={{ marginTop: smallerScreen ? '36px' : 0 }}>
          <SelectDropdown
            label={t('categoryTree')}
            handleChange={(value: string) => {
              setFieldValue('categoryTree', value)
            }}
            selectedValue={values.categoryTree}
            selectOptions={categoryTrees}
            marginBottom='0px'
          />
        </Box>
        <Box
          sx={{ marginTop: smallerScreen ? '16px' : '20px' }}
          width={smallerScreen ? '100%' : '33%'}>
          <SearchBar
            setPage={setPage}
            input={values.search}
            setInput={setFieldValue}
            values={values}
            handleClick={submitForm}
            ariaLabel={t('search')}
            placeholderText={t('search')}
          />
        </Box>
      </Box>
      {isMobile && (
        <Box display='flex' justifyContent='flex-end'>
          <ForwardButtonWrapper
            aria-label={t('adjustConfig-Aria')}
            onClick={() => setShowOverlay(true)}>
            <Typography ml={1}>{t('viewConfiguration')}</Typography>
            <ArrowForward />
          </ForwardButtonWrapper>
        </Box>
      )}
    </Box>
  )
}
